import memoizeOne from 'memoize-one';
import React, { Component } from 'react';
import { Pagination } from '~/Api/Pagination';
import { Item as ProductItem, OrderDirection, Product, ProductsStats, SortField } from '~/Api/Product';
import { SelectionTypes } from '~/GroupProduct';
import FilterParameter from '~/libs/FilterParameter/FilterParameter';
import { selectCardinalPlural } from '~/libs/Intl';
import { PricedProduct, Query } from '~/Product';
import { ProductProvider } from '~/Product/ProductProvider';
import ProductDetailModal from '~/ProductDetailModal';
import ProductFilterForm from '~/ProductFilterForm';
import { ProductFilterFormFilters } from '~/ProductFilterForm/ProductFilterForm';
import ProductGroupPanel from '~/ProductGroupPanel';
import { MissingPricesTooltip } from '~/ProductPrice';
import Header from '~/ProductSearch/Header/Header';
import ProductsTable, { Header as TableHeader, Panel, ProductCheckboxAccessory, SortFields } from '~/ProductsTable';
import panelStyles from '~/ProductsTable/Panel/styles.less';
import ActivityIndicator from '~/UI/ActivityIndicator';
import Button from '~/UI/Button';
import linkButtonStyles from '~/UI/Button/link.less';
import Checkbox from '~/UI/Checkbox/Checkbox';
import DocumentTitle from '~/UI/DocumentTitle/DocumentTitle';
import Heading from '~/UI/Heading/Heading';
import LayoutConstraint from '~/UI/LayoutConstraint/LayoutConstraint';
import ShareLinkPopover from '~/UI/ShareLinkPopover/ShareLinkPopover';
import SimplePaginator from '~/UI/SimplePaginator/SimplePaginator';

import styles from './styles.less';

type PricedProductItem = PricedProduct<ProductItem>;
export interface Props {
	currency: string | null;
	priceType: string | null;
	filterCalculatedPrice: boolean;
	queryErrors: { [key in string]: string[] };
	query: Query;
	sortFields: SortFields;
	products: PricedProductItem[];
	hasNextPage: boolean;
	isFetchingProducts: boolean;
	isFetchingProductPrices: boolean;
	isSaving: boolean;
	pagination: Pagination | null;
	paginationLimitReached: boolean;
	stats: ProductsStats | null;
	productId: string | null;
	productTabName?: string;
	missingPricesPercentage: number;
	productDetailLink(product: Product): { pathname: string; search: string };
	searchLink(
		query: Query,
		currency: string | null,
		priceType: string | null,
		filterCalculatedPrice: boolean,
		productId: string | null
	): { pathname: string; search: string };
	onFetch(query: Query, priceType: string | null, filterCalculatedPrice: boolean): void;
	onFetchPrices(a: string | null, b: string[]): void;
	onPrune(query: Query): void;
	onSearch(
		query: Query,
		productId: string | null,
		priceType: string | null,
		currentPriceType: string | null,
		currency: string | null,
		filterCalculatedPrice: boolean
	): void;
	onCloseDetail(): void;
	onShowDetail(a: string, b: string): void;
	onShare(): void;
	onProductSelected(item: PricedProductItem | null): void;
}

interface State {
	selectedProducts: Set<Product>;
	allSelection: 'all' | 'loaded' | null;
	selectedProductsHash: number;
	showSharePopover: boolean;
	lastSeenProductIdDetail: string | null;
}

export default class SearchPage extends Component<Props, State> {
	static defaultProps: Pick<Props, 'currency' | 'priceType' | 'pagination' | 'stats' | 'productId' | 'productTabName'> =
		{
			currency: null,
			priceType: null,
			pagination: null,
			stats: null,
			productId: null,
			productTabName: 'info',
		};

	state: State = {
		selectedProducts: new Set<Product>(),
		allSelection: null,
		selectedProductsHash: 0,
		showSharePopover: false,
		lastSeenProductIdDetail: null,
	};

	sharePopoverContainerElement: HTMLSpanElement | null = null;

	productsTableHeader: { current: TableHeader | null } = React.createRef();

	componentDidMount() {
		this.fetchProducts(this.props);
	}

	componentDidUpdate(prevProps: Props) {
		const { currency, query, products, onFetchPrices, onPrune } = this.props;

		if (!query.equals(prevProps.query)) {
			onPrune(prevProps.query);
			this.fetchProducts(this.props);
		} else if (currency !== prevProps.currency && products.length > 0) {
			onFetchPrices(
				currency,
				products.map((product) => product.product.id)
			);
		}

		if (products !== prevProps.products) {
			const productIds = new Set(products.map((product) => product.product.id));

			this.setState((state) => ({
				selectedProducts: new Set(Array.from(state.selectedProducts).filter((product) => productIds.has(product.id))),
				allSelection: null,
			}));
		}
	}

	componentWillUnmount() {
		const { query, onPrune } = this.props;
		onPrune(query);
	}

	fetchProducts(props: Props) {
		const { query, priceType, filterCalculatedPrice, onFetch } = props;
		onFetch(query, priceType, filterCalculatedPrice);
	}

	render() {
		const {
			props: {
				currency,
				priceType,
				filterCalculatedPrice,
				sortFields,
				query,
				products,
				pagination,
				hasNextPage,
				isFetchingProducts,
				isFetchingProductPrices,
				productId,
				productTabName,
				isSaving,
				onShowDetail,
			},
			state: { selectedProducts, allSelection, selectedProductsHash, lastSeenProductIdDetail },
		} = this;

		return (
			<div className={selectedProducts.size > 0 ? styles.containerWithPanel : styles.container}>
				<DocumentTitle title='Search' />

				<section className={styles.section}>
					<ProductsTable
						accessory={this.renderRowAccessory(selectedProductsHash, isSaving)}
						accessoryHeader={this.renderRowAccessoryHeader(allSelection, isSaving)}
						currency={currency}
						detailLink={this.createDetailLink}
						filterCalculatedPrice={filterCalculatedPrice}
						footer={isFetchingProducts ? <ActivityIndicator /> : hasNextPage ? this.renderPaginator() : null}
						footerHeight={65}
						header={this.renderHeader()}
						headRowRef={this.productsTableHeader}
						highlightProductId={lastSeenProductIdDetail}
						initialHeaderHeight={231 + query.filterParameters.length * 65}
						isFetching={isFetchingProductPrices}
						orderDirection={query.order}
						panel={this.renderPanel()}
						priceHeader={this.renderPriceHeader()}
						priceType={priceType}
						products={products}
						sortField={query.sortField}
						sortFields={sortFields}
						sortLink={this.createSortLink}
						onPriceChange={this.handlePriceChange}
					/>
				</section>

				{selectedProducts.size > 0 || allSelection !== null ? (
					<ProductGroupPanel
						selectedProductsCount={allSelection === SelectionTypes.all ? pagination?.total || 0 : selectedProducts.size}
						selection={
							allSelection === SelectionTypes.all
								? { type: SelectionTypes.all, query }
								: { type: SelectionTypes.products, products: selectedProducts, query }
						}
						onAddProductsToGroup={this.handleAddProductsToGroup}
					/>
				) : null}

				{productId !== null ? (
					<ProductProvider id={productId}>
						{(product) => (
							<ProductDetailModal
								currency={currency}
								filterParameters={query.filterParameters}
								groupId={null}
								isFetching={isFetchingProducts}
								isFetchingPrices={isFetchingProductPrices}
								product={product}
								productId={productId}
								products={products}
								selectedTabName={productTabName}
								onClose={this.handleCloseDetail}
								onCurrencyChange={(currency) => this.handlePriceChange(currency, priceType, filterCalculatedPrice)}
								onChange={(productId) => onShowDetail(productId, productTabName ?? 'info')}
								onSelectTab={(tabName) => onShowDetail(productId, tabName)}
							/>
						)}
					</ProductProvider>
				) : null}
			</div>
		);
	}

	renderRowAccessory = memoizeOne((selectedProductsHash, isSaving) => (pricedProduct: PricedProduct<ProductItem>) => {
		const {
			state: { selectedProducts },
		} = this;

		return (
			<ProductCheckboxAccessory
				disabled={isSaving}
				initialChecked={selectedProducts.has(pricedProduct.product)}
				key={selectedProductsHash}
				pricedProduct={pricedProduct}
				onChange={this.handleSelectedProductChange}
			/>
		);
	});

	renderRowAccessoryHeader = memoizeOne((allSelection, isSaving) => (
		<Checkbox
			disabled={isSaving}
			checked={Boolean(allSelection !== null)}
			id='select-all-products'
			name='select-all-products'
			onChange={this.handleSelectAllProductsChange}
		/>
	));

	createDetailLink = (product: Product) => this.props.productDetailLink(product);

	createSortLink = (sortField: SortField, orderDirection: OrderDirection) => {
		const { productId, searchLink, query, currency, priceType, filterCalculatedPrice } = this.props;
		const newQuery = new Query(query.filterParameters, sortField, orderDirection);
		return searchLink(newQuery, currency, priceType, filterCalculatedPrice, productId);
	};

	renderHeader() {
		const {
			props: { query, queryErrors, pagination, stats },
		} = this;
		return this.createHeader(query, queryErrors, pagination, stats);
	}

	createHeader = memoizeOne(
		(
			query: Query,
			queryErrors: { [param: string]: string[] },
			pagination: Pagination | null,
			stats: ProductsStats | null
		) => {
			return (
				<Header>
					<ProductFilterForm
						errors={queryErrors}
						filterParameters={query.filterParameters as ProductFilterFormFilters}
						key='search'
						onChange={(filterParameters) => this.search(filterParameters)}
						onSearch={(filterParameters) => this.search(filterParameters)}
					/>

					{pagination !== null && stats !== null ? (
						<Heading>
							Found {pagination.total} {selectCardinalPlural(pagination.total, ['product', 'products'])}
							{' in '}
							{stats.countriesCount} {selectCardinalPlural(stats.countriesCount, ['country', 'countries'])}{' '}
							{this.renderShareButton()}
						</Heading>
					) : null}
				</Header>
			);
		}
	);

	renderPriceHeader() {
		const { priceType, filterCalculatedPrice, query, missingPricesPercentage, productId } = this.props;

		return productId === null ? (
			<MissingPricesTooltip
				key={`${String(priceType)}-${String(filterCalculatedPrice)}-${query.key}`}
				missingPricesPercentage={missingPricesPercentage}
				reference={<span>Price</span>}
				onClose={() => {
					const { productId, currency, query, filterCalculatedPrice, onSearch } = this.props;
					if (this.productsTableHeader.current) {
						this.productsTableHeader.current.showPriceSelect();
					}
					onSearch(query, productId, null, null, currency, filterCalculatedPrice);
				}}
			/>
		) : null;
	}

	renderPanel() {
		const {
			props: { products, pagination, isSaving },
			state: { allSelection, selectedProducts },
		} = this;
		return this.createPanel(products, pagination, allSelection, selectedProducts, isSaving);
	}

	createPanel = memoizeOne((products, pagination, allSelection, selectedProducts, isSaving) => {
		if (products.length === 0 || isSaving) {
			return null;
		}

		if (
			allSelection === 'loaded' &&
			selectedProducts.size > 0 &&
			selectedProducts.size < pagination.total &&
			products.length === selectedProducts.size
		) {
			return (
				<Panel>
					All {selectedProducts.size} products on loaded pages selected.{' '}
					<Button styles={panelStyles} onClick={() => this.setState({ allSelection: 'all' })}>
						Select all {pagination.total} products
					</Button>
					.
				</Panel>
			);
		}

		if (allSelection === 'all') {
			return (
				<Panel>
					All products in this search are selected.{' '}
					<Button styles={panelStyles} onClick={() => this.setState({ allSelection: 'loaded' })}>
						Select only products from loaded pages
					</Button>
					.
				</Panel>
			);
		}

		return null;
	});

	renderShareButton() {
		const {
			state: { showSharePopover },
			props: { onShare },
		} = this;
		const button = (
			<Button
				styles={linkButtonStyles}
				onClick={() => {
					if (!showSharePopover) {
						onShare();
					}
					this.setState({ showSharePopover: !showSharePopover });
				}}
			>
				Share
			</Button>
		);

		return (
			<span ref={(ref) => (this.sharePopoverContainerElement = ref)}>
				<LayoutConstraint reference={button}>
					{showSharePopover ? <ShareLinkPopover onClose={this.handleCloseSharePopover} /> : null}
				</LayoutConstraint>
			</span>
		);
	}

	renderPaginator() {
		const { pagination, paginationLimitReached } = this.props;

		return paginationLimitReached ? (
			<p className={styles.maxProductsNote}>Maximum products loaded, please refine your search.</p>
		) : (
			<SimplePaginator count={pagination?.nextGrowedPage?.perPage} onLoadMore={() => this.fetchProducts(this.props)} />
		);
	}

	search(filterParameters: FilterParameter<string | string[]>[]) {
		const { productId, query, currency, priceType, filterCalculatedPrice, onSearch } = this.props;
		this.setState((prevState) => ({
			selectedProducts: new Set(),
			allSelection: null,
			selectedProductsHash: prevState.selectedProductsHash + 1,
		}));

		const newQuery = new Query(filterParameters, query.sortField, query.order);
		onSearch(newQuery, productId, priceType, priceType, currency, filterCalculatedPrice);
	}

	handlePriceChange = (currency: string | null, priceType: string | null, filterCalculatedPrice: boolean) => {
		const { productId, query, priceType: currentPriceType, onSearch } = this.props;
		onSearch(query, productId, priceType, currentPriceType, currency, filterCalculatedPrice);
	};

	handleSelectedProductChange = (pricedProduct: PricedProductItem, isSelected: boolean) => {
		const {
			state: { allSelection, selectedProducts },
			props: { onProductSelected },
		} = this;
		const newSelectedProducts = new Set(selectedProducts);
		if (isSelected) {
			newSelectedProducts.add(pricedProduct.product);
		} else {
			newSelectedProducts.delete(pricedProduct.product);
		}

		this.setState({
			allSelection: allSelection !== null ? 'loaded' : null,
			selectedProducts: newSelectedProducts,
		});

		if (isSelected) {
			onProductSelected(pricedProduct);
		}
	};

	handleSelectAllProductsChange = (checked: boolean) => {
		this.setState((prevState) => ({
			allSelection: checked ? 'all' : null,
			selectedProducts: checked
				? new Set(this.props.products.map((pricedProduct) => pricedProduct.product))
				: new Set(),
			selectedProductsHash: prevState.selectedProductsHash + 1,
		}));

		if (checked) {
			this.props.onProductSelected(null);
		}
	};

	handleAddProductsToGroup = () => {
		this.setState((prevState) => ({
			allSelection: null,
			selectedProducts: new Set(),
			selectedProductsHash: prevState.selectedProductsHash + 1,
		}));
	};

	handleCloseSharePopover = (event: MouseEvent) => {
		if (
			this.sharePopoverContainerElement !== null &&
			event.target !== this.sharePopoverContainerElement &&
			// @ts-expect-error Argument of type 'EventTarget | null' is not assignable to parameter of type 'Node | null'.
			!this.sharePopoverContainerElement.contains(event.currentTarget)
		) {
			this.setState({ showSharePopover: false });
		}
	};

	handleCloseDetail = () => {
		const { productId, onCloseDetail } = this.props;

		this.setState({ lastSeenProductIdDetail: productId });
		onCloseDetail();
	};
}
